import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import PaymentTrackActions from "../../Redux/transition/payment-track/payment-track.reducer";
import MembershipActions from "../../Redux/transition/membership/membership.reducer";
import DefMembershipActions from "../../Redux/actions/defMembership-actions";
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import CustomDataTable from "../../Components/CustomDataTable";
import { FormatCurrency, CurrencyFormatter, useCurrencyConstants } from '../../Components/CurrencyFormatter';
import FilterDropdown from "../../Components/DateRange";
import { getDateRange, isLifetimePeriod, resolveLifetimeStart } from "../../Components/DateRange";
import { useDateFormatter } from "../../Components/FormatDateTime"
import "../../Styles/pages/product.scss";
import "../../Styles/pages/payments.scss";

const EXTERNAL_SUMMARY_MOBILE_BREAKPOINT = 1050;

const EXTERNAL_MODE_COLUMNS = [
    { field: 'cash', modeKey: 'payments.external_mode_cash' },
    { field: 'mobileMoney', modeKey: 'payments.external_mode_mobile_money' },
    { field: 'bankTransfer', modeKey: 'payments.external_mode_bank_transfer' },
    { field: 'cheque', modeKey: 'payments.external_mode_cheque' },
    { field: 'unknown', modeKey: 'payments.external_mode_unknown' },
];

const ExternalSummary = () => {

    const { t } = useTranslation("common");
    const { currencyFormat, currencySymbol } = useCurrencyConstants();
    const toast = useRef(null);
    const { formatApiDate, formatDate } = useDateFormatter();

    const periods = [
        { code: "Today", value: t("dashboard.today") },
        { code: "Last week", value: t("dashboard.last_week") },
        { code: "This month", value: t("dashboard.this_month") },
        { code: "Last month", value: t("dashboard.last_month") },
        { code: "Last 3 months", value: t("dashboard.3_months_duration") },
        { code: "Last 6 months", value: t("dashboard.6_months_duration") },
        { code: "Last 12 months", value: t("dashboard.12_months_duration") },
        { code: "Year to date", value: t("dashboard.year_to_date") },
        { code: "Lifetime", value: t("dashboard.lifetime") },
        { code: "Custom date", name: t("dashboard.custom_date") }
    ];

    const [calendarDates, setCalendarDates] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [cleared, setCleared] = useState(false);
    const [duration, setDuration] = useState(t("dashboard.this_month"));
    const [showCustomDate, setShowCustomDate] = useState(false);
    const [first, setFirst] = useState(0);
    const [pageLoading, setPageLoading] = useState(false);
    const rows = 15;

    const [currentDateRange, setCurrentDateRange] = useState({ startDate: null, endDate: null });
    const [selectedColleagueId, setSelectedColleagueId] = useState(null);

    const [csvRequested, setCsvRequested] = useState(false);
    const [pdfRequested, setPdfRequested] = useState(false);
    const [isMobileStack, setIsMobileStack] = useState(
        () => typeof window !== "undefined" && window.innerWidth <= EXTERNAL_SUMMARY_MOBILE_BREAKPOINT
    );

    useEffect(() => {
        const mediaQuery = window.matchMedia(`(max-width: ${EXTERNAL_SUMMARY_MOBILE_BREAKPOINT}px)`);
        const onBreakpointChange = (event) => setIsMobileStack(event.matches);
        onBreakpointChange(mediaQuery);
        mediaQuery.addEventListener("change", onBreakpointChange);
        return () => mediaQuery.removeEventListener("change", onBreakpointChange);
    }, []);

    const dispatch = useDispatch();
    const getExternalColleagueSummary = useCallback((businessId, startDate, endDate, membershipId) => {
        dispatch(PaymentTrackActions.externalColleagueSummaryRequest(businessId, startDate, endDate, membershipId || null));
    }, [dispatch]);
    const getExternalColleagueSummaryCsv = useCallback((businessId, startDate, endDate, membershipId) => {
        dispatch(PaymentTrackActions.externalColleagueSummaryCsvRequest(businessId, startDate, endDate, membershipId || null));
    }, [dispatch]);
    const getExternalColleagueSummaryPdf = useCallback((businessId, membershipId, startDate, endDate, recordedById) => {
        dispatch(PaymentTrackActions.externalColleagueSummaryPdfRequest(businessId, membershipId, startDate, endDate, recordedById || null));
    }, [dispatch]);
    const getDefMembership = useCallback(() => { dispatch(DefMembershipActions.defMembershipRequest()); }, [dispatch]);
    const getColleagues = useCallback((businessId) => { dispatch(MembershipActions.colleaguesRequest(businessId)); }, [dispatch]);

    const defMembership = useSelector((state) => state.defMembership.defMembership);
    const colleagues = useSelector((state) => state.memberships.colleagues);
    const {
        externalColleagueSummary,
        fetchingExternalColleagueSummary,
        externalColleagueSummaryCsv,
        fetchingExternalColleagueSummaryCsv,
        errorExternalColleagueSummaryCsv,
        externalColleagueSummaryPdf,
        fetchingExternalColleagueSummaryPdf,
        errorExternalColleagueSummaryPdf,
    } = useSelector((state) => state.paymentTracks);

    const businessId = defMembership?.business?.id;
    const planGroupsGraphedAll = useSelector((state) => state.planGroups.planGroupsGraphedAll);
    const planGroupsGraphedActive = useSelector((state) => state.planGroups.planGroupsGraphedActive);
    const lifetimeStart = useMemo(
        () => resolveLifetimeStart({ graphed: planGroupsGraphedAll || planGroupsGraphedActive }),
        [planGroupsGraphedAll, planGroupsGraphedActive]
    );

    const colleagueOptions = useMemo(() => {
        if (!colleagues?.length) return [];
        return colleagues.map((colleague) => ({
            label: colleague.user?.name || colleague.name || String(colleague.id),
            value: String(colleague.id ?? colleague.membershipId),
        }));
    }, [colleagues]);

    const dropdownExportItems = useMemo(() => [
        { name: t("payments.download_as_pdf"), code: "PDF", alias: t("payments.pdf_hint") },
        { name: t("payments.download_as_csv"), code: "CSV", alias: t("payments.csv_hint") },
    ], [t]);

    useEffect(() => {
        if (!defMembership) {
            getDefMembership();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (businessId) {
            getColleagues(businessId);
        }
    }, [businessId, getColleagues]);

    useEffect(() => {
        if (csvRequested && externalColleagueSummaryCsv && !fetchingExternalColleagueSummaryCsv) {
            const url = window.URL.createObjectURL(new Blob([externalColleagueSummaryCsv], { type: 'text/csv' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `external_summary_report_${currentDateRange.startDate}_${currentDateRange.endDate}.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.current.show({
                severity: 'success',
                summary: t('success'),
                detail: t('plan.external_summary_csv_download_success'),
                life: 3000
            });

            setCsvRequested(false);
        }
    }, [externalColleagueSummaryCsv, fetchingExternalColleagueSummaryCsv, csvRequested, currentDateRange, t]);

    useEffect(() => {
        if (csvRequested && errorExternalColleagueSummaryCsv && !fetchingExternalColleagueSummaryCsv) {
            toast.current.show({
                severity: 'error',
                summary: t('error.error'),
                detail: t('plan.external_summary_csv_download_error'),
                life: 3000
            });

            setCsvRequested(false);
        }
    }, [errorExternalColleagueSummaryCsv, fetchingExternalColleagueSummaryCsv, csvRequested, t]);

    useEffect(() => {
        if (pdfRequested && externalColleagueSummaryPdf && !fetchingExternalColleagueSummaryPdf) {
            const url = window.URL.createObjectURL(new Blob([externalColleagueSummaryPdf], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `external_summary_report_${currentDateRange.startDate}_${currentDateRange.endDate}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.current.show({
                severity: 'success',
                summary: t('success'),
                detail: t('plan.external_summary_pdf_download_success'),
                life: 3000
            });

            setPdfRequested(false);
        }
    }, [externalColleagueSummaryPdf, fetchingExternalColleagueSummaryPdf, pdfRequested, currentDateRange, t]);

    useEffect(() => {
        if (pdfRequested && errorExternalColleagueSummaryPdf && !fetchingExternalColleagueSummaryPdf) {
            toast.current.show({
                severity: 'error',
                summary: t('error.error'),
                detail: t('plan.external_summary_pdf_download_error'),
                life: 3000
            });

            setPdfRequested(false);
        }
    }, [errorExternalColleagueSummaryPdf, fetchingExternalColleagueSummaryPdf, pdfRequested, t]);

    const getDates = (range) => {
        const { startDate, endDate } = getDateRange(range, { returnRange: true, lifetimeStart });
        if (!startDate || !endDate) return null;

        return {
            startDate: formatApiDate(startDate),
            endDate: formatApiDate(endDate)
        };
    };

    const fetchExternalSummaryReport = useCallback((range, colleagueId = selectedColleagueId) => {
        if (!businessId) return;
        const dates = getDates(range);
        if (!dates?.startDate || !dates?.endDate) return;
        setCurrentDateRange(dates);
        getExternalColleagueSummary(businessId, dates.startDate, dates.endDate, colleagueId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId, selectedColleagueId, getExternalColleagueSummary, lifetimeStart]);

    const onPageChange = (event) => {
        if (event.first !== first) {
            setPageLoading(true);
            setFirst(event.first);

            setTimeout(() => {
                setPageLoading(false);
            }, 200);
        }
    };

    useEffect(() => {
        if (businessId) {
            fetchExternalSummaryReport(duration);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId]);

    const onDurationChange = (e) => {
        setDuration(e.value);
        const periodValue = e.value;
        if (periodValue.code !== 'Custom date') {
            setCalendarDates(null);
            setShowCustomDate(false);
            if (!isLifetimePeriod(periodValue)) {
                fetchExternalSummaryReport(e.value);
            }
        } else setShowCustomDate(true);
    }

    useEffect(() => {
        if (!isLifetimePeriod(duration) || !lifetimeStart || !businessId) return;
        fetchExternalSummaryReport(duration);
    }, [duration, lifetimeStart, businessId, fetchExternalSummaryReport]);

    const dateRangeForTotals = getDateRange(duration, { returnRange: true, lifetimeStart })
    const start = formatDate(calendarDates ? calendarDates[0] : dateRangeForTotals?.startDate);
    const end = formatDate(calendarDates ? calendarDates[1] : dateRangeForTotals?.endDate)

    const handleCalendarSelect = (e) => {
        const range = e.value;
        setCalendarDates(range);
        if (range && range[1] !== null && range.length === 2) {
            const [startDate, endDate] = range;
            const formattedStart = formatApiDate(startDate);
            const formattedEnd = formatApiDate(endDate);
            setCurrentDateRange({ startDate: formattedStart, endDate: formattedEnd });
            getExternalColleagueSummary(businessId, formattedStart, formattedEnd, selectedColleagueId);
        }
    };

    const handleColleagueSelect = (e) => {
        const colleagueId = e.value || null;
        setSelectedColleagueId(colleagueId);
        setFirst(0);

        if (calendarDates && calendarDates[1] !== null && calendarDates.length === 2) {
            const formattedStart = formatApiDate(calendarDates[0]);
            const formattedEnd = formatApiDate(calendarDates[1]);
            setCurrentDateRange({ startDate: formattedStart, endDate: formattedEnd });
            getExternalColleagueSummary(businessId, formattedStart, formattedEnd, colleagueId);
            return;
        }

        if (duration?.code === 'Custom date') {
            return;
        }

        fetchExternalSummaryReport(duration, colleagueId);
    };

    const warnMissingDateRange = () => {
        toast.current.show({
            severity: 'warn',
            summary: t('common.warning'),
            detail: t('plan.select_date_range'),
            life: 3000
        });
    };

    const handleExportCsv = () => {
        if (!currentDateRange.startDate || !currentDateRange.endDate) {
            warnMissingDateRange();
            return;
        }
        setCsvRequested(true);
        getExternalColleagueSummaryCsv(businessId, currentDateRange.startDate, currentDateRange.endDate, selectedColleagueId);
    };

    const handleExportPdf = () => {
        if (!currentDateRange.startDate || !currentDateRange.endDate) {
            warnMissingDateRange();
            return;
        }
        if (!defMembership?.id) {
            toast.current.show({
                severity: 'error',
                summary: t('error.error'),
                detail: t('plan.external_summary_pdf_download_error'),
                life: 3000
            });
            return;
        }
        setPdfRequested(true);
        getExternalColleagueSummaryPdf(
            businessId,
            defMembership.id,
            currentDateRange.startDate,
            currentDateRange.endDate,
            selectedColleagueId
        );
    };

    const exportItemsTemplate = (option) => {
        const handleExportClick = () => {
            if (option?.code === 'PDF') {
                handleExportPdf();
            } else {
                handleExportCsv();
            }
        };

        return (
            <section className='flex flex-column mb-1' onClick={() => handleExportClick()}>
                <div className='dropdown-export-temp pt-1'>
                    <p className='m-0 option-name'>{option.name}</p>
                    <i className='pi pi-download' />
                </div>
                <p className='m-0 option-alias'>{option.alias}</p>
            </section>
        );
    };

    const amountTemplate = (amount) => (
        <div className="flex text-align-left">
            <FormatCurrency
                amount={amount}
                currencySymbol={currencySymbol}
                currencyFormat={currencyFormat}
                className='planCollected'
            />
        </div>
    );

    const colleagueBodyTemplate = (rowData) => (
        <div className="flex text-align-left gap-2" id="plan-body-div">
            <span className="plan_body">{rowData.colleagueName || t("plan.unassigned_colleague")}</span>
        </div>
    );

    const externalSummaryMobileTemplate = (rowData) => (
        <div className="payment-summary-mobile-summary">
            <div className="payment-summary-mobile-section">{colleagueBodyTemplate(rowData)}</div>
            {EXTERNAL_MODE_COLUMNS.map(({ field }) => (
                <div key={field} className="payment-summary-mobile-section">{amountTemplate(rowData[field])}</div>
            ))}
            <div className="payment-summary-mobile-section payment-summary-mobile-section-last">{amountTemplate(rowData.total)}</div>
        </div>
    );

    const desktopColumns = [
        {
            field: "colleague",
            header: t("payments.record_by"),
            body: colleagueBodyTemplate,
            alignHeader: "left"
        },
        ...EXTERNAL_MODE_COLUMNS.map(({ field, modeKey }) => ({
            field,
            header: t(modeKey),
            body: (rowData) => amountTemplate(rowData[field]),
            alignHeader: "left"
        })),
        {
            field: 'total',
            header: t("payments.total"),
            body: (rowData) => amountTemplate(rowData.total),
            alignHeader: "left"
        }
    ];

    const mobileSummaryColumn = {
        field: "mobileSummary",
        header: t("payments.record_by"),
        body: externalSummaryMobileTemplate,
        className: "payment-summary-mobile-summary-column",
        alignHeader: "left",
    };

    const columns = isMobileStack ? [mobileSummaryColumn] : desktopColumns;
    const exportBusy = fetchingExternalColleagueSummaryCsv || fetchingExternalColleagueSummaryPdf;
    const loading = fetchingExternalColleagueSummary;

    return (
        <div className="products-screen payment-summary-screen">
            <Toast ref={toast} />

            <Card id="payment-action-bar" className="plan_action_card payment-summary-card">
                <div className="payment-summary-filters">
                    <FilterDropdown
                        value={duration}
                        options={periods}
                        onChange={onDurationChange}
                        optionLabel="code"
                        autoSetActiveId={true}
                        id="external-summary-period"
                        showHint={true}
                        hintText={t("plan.reporting_period_hint")}
                        className="activity-dropdown text-left"
                        containerClass="payment-summary-period-filter filters"
                    />

                    {showCustomDate && (
                        <div className="payment-summary-calendar plan-filter-calendar">
                            <Calendar
                                className="p-datepicker-header report-calender datepicker"
                                id='external-summary-calendar'
                                value={calendarDates}
                                showIcon
                                showButtonBar
                                onClearButtonClick={() => { setCleared(true) }}
                                dateFormat="dd M yy"
                                maxDate={new Date()}
                                onChange={handleCalendarSelect}
                                selectionMode="range"
                                readOnlyInput
                                placeholder={t("report.choose_date")}
                            />
                            <p className="reporting_period_hint">{t("plan.reporting_period_hint")}</p>
                        </div>
                    )}

                    <div className="payment-summary-period-filter filters payments-colleague-filter">
                        <div className="p-inputgroup">
                            <Dropdown
                                placeholder={t("payments.record_by")}
                                className="items-center activity-dropdown payments-filter-dropdown recorded-by-dropdown text-left"
                                panelClassName="recorded-by-dropdown-panel"
                                options={colleagueOptions}
                                optionLabel="label"
                                optionValue="value"
                                value={selectedColleagueId}
                                onChange={handleColleagueSelect}
                                filter
                                filterBy="label"
                                filterPlaceholder={t("search")}
                                showClear
                                disabled={colleagueOptions.length === 0}
                            />
                        </div>
                    </div>
                </div>

                <hr className="hr_payment_summary" />

                <div className="action_bar_totals payment-summary-totals">
                    {(start || end) && <p className="total_hint_style">{t("plan.total_hint", { startDate: start, endDate: end })}</p>}

                    <div className="payment-summary-totals-row">
                        <div className="payment-summary-totals-grid">
                            <div className="payment-summary-total-item">
                                <span className="green-success-txts">{t("payments.total")}</span><br />
                                <CurrencyFormatter
                                    amount={externalColleagueSummary?.total}
                                    currencySymbol={currencySymbol}
                                    currencyFormat={currencyFormat}
                                    valueClass="amt-fig p-1"
                                    symbolClass="count-curren p-0"
                                />
                            </div>
                            {EXTERNAL_MODE_COLUMNS.map(({ field, modeKey }) => (
                                <div key={field} className="payment-summary-total-item">
                                    <span className="bold_number_text">{t(modeKey)}</span><br />
                                    <CurrencyFormatter
                                        amount={externalColleagueSummary?.[field]}
                                        currencySymbol={currencySymbol}
                                        currencyFormat={currencyFormat}
                                        valueClass="amt-fig p-1"
                                        symbolClass="count-curren p-0"
                                    />
                                </div>
                            ))}
                            <div className="payment-summary-total-item">
                                <span className="bold_number_text">{t("plan.external_payments")}</span><br />
                                <span className="bold_number_value">{externalColleagueSummary?.paymentCount ?? 0}</span>
                            </div>
                        </div>
                        <div className="payment-summary-export plan-filter-export">
                            <div className="spin_div payments-export-row">
                                <Dropdown
                                    placeholder={t('export')}
                                    style={{ width: "130px" }}
                                    options={dropdownExportItems}
                                    optionLabel="name"
                                    itemTemplate={exportItemsTemplate}
                                    className='text-left export-dropdown items-center'
                                    id="export_drop"
                                    disabled={exportBusy}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            <CustomDataTable
                key={`external-summary-table-${isMobileStack}`}
                value={externalColleagueSummary?.summaries}
                columns={columns}
                paginator
                first={first}
                rows={rows}
                loading={pageLoading || loading}
                onPage={onPageChange}
                className="customed-datatable payment-summary-data-table"
                responsiveLayout="stack"
                scrollable={!isMobileStack}
            />
        </div>
    )
};
export default ExternalSummary;
